/**
 * Maps community schedule API shapes ↔ Schedule a Cleaning UI state.
 */

import { createEmptyCleaningAreaSelection } from './cleaningAreaData';

/**
 * API slot index: stored as shift_index (int, 0-based); GET aliases as `index`.
 * UI tab label "Shift - N" uses N = zeroBasedIndex + 1.
 */
export const resolveZeroBasedShiftIndex = item => {
  if (typeof item?.index === 'number') return item.index;
  if (typeof item?.shift_index === 'number') return item.shift_index;
  const parsed = Number.parseInt(item?.shift_index, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PERSONNEL_API_TO_UI = {
  supervisors: 'facility-team',
  employees: 'emp-and-other',
  housekeeping: 'house-keeping',
  security: 'security',
};

export const floorIdFromNo = floorNo => `floor-${floorNo}`;

export const floorNoFromId = floorId => {
  const match = /^floor-(\d+)$/.exec(floorId || '');
  return match ? parseInt(match[1], 10) : null;
};

const defaultFloorLabel = floorNo =>
  floorNo === 0 ? 'Ground Floor' : `${floorNo}${floorNo === 1 ? 'st' : floorNo === 2 ? 'nd' : floorNo === 3 ? 'rd' : 'th'} Floor`;

/** Unwrap API Gateway / proxy JSON shapes to a plain object. */
export const normalizeCommunityApiPayload = payload => {
  if (payload == null) return {};
  if (typeof payload === 'string') {
    try {
      return normalizeCommunityApiPayload(JSON.parse(payload));
    } catch {
      return {};
    }
  }
  if (payload.body != null) {
    return normalizeCommunityApiPayload(payload.body);
  }
  if (payload.data && typeof payload.data === 'object') {
    return normalizeCommunityApiPayload(payload.data);
  }
  return payload;
};

const mapFloorEntry = (floor, idx) => {
  const floorNo =
    typeof floor?.floor_no === 'number'
      ? floor.floor_no
      : typeof floor?.floorNo === 'number'
        ? floor.floorNo
        : idx;
  return {
    id: floorIdFromNo(floorNo),
    floor_no: floorNo,
    label: floor?.floor_name || floor?.floorName || defaultFloorLabel(floorNo),
  };
};

const mapBlockFloors = block => {
  const explicitFloors = block.floors || block.floor_list || block.floorList;
  if (Array.isArray(explicitFloors) && explicitFloors.length > 0) {
    return explicitFloors.map(mapFloorEntry);
  }

  const meta = block.block_metadata || block.metadata || block.blockMetadata || {};
  const floorCount =
    typeof meta.floors === 'number'
      ? meta.floors
      : typeof block.floor_count === 'number'
        ? block.floor_count
        : typeof block.floors === 'number'
          ? block.floors
          : 0;

  if (floorCount > 0) {
    return Array.from({ length: floorCount }, (_, floorNo) => ({
      id: floorIdFromNo(floorNo),
      floor_no: floorNo,
      label: defaultFloorLabel(floorNo),
    }));
  }

  return [];
};

const mapBlockEntry = block => {
  const id = block.block_id || block.blockId || block.id;
  if (!id) return null;

  return {
    id,
    label:
      block.block_name ||
      block.blockName ||
      block.name ||
      block.label ||
      id,
    block_type: block.block_type || block.blockType,
    floors: mapBlockFloors(block),
  };
};

/** @param {{ blocks?: object[], amenities?: object[] }} locations */
export const mapLocationsToSiteCatalog = locations => {
  const payload = normalizeCommunityApiPayload(locations);
  const rawBlocks = [
    ...(Array.isArray(payload.blocks) ? payload.blocks : []),
    ...(Array.isArray(payload.streets) ? payload.streets : []),
  ];

  const blocks = rawBlocks.map(mapBlockEntry).filter(Boolean);

  const rawAmenities = payload.amenities || payload.amenity_list || [];
  const amenities = (Array.isArray(rawAmenities) ? rawAmenities : []).map(amenity => ({
    id: amenity.amenity_id || amenity.amenityId || amenity.id,
    label: amenity.amenity_name || amenity.amenityName || amenity.name || amenity.amenity_id,
    amenity_type: amenity.amenity_type || amenity.amenityType,
  })).filter(a => a.id);

  return { blocks, amenities };
};

/** @param {{ templates?: object[] }} templatesResponse */
export const mapTaskTemplatesToCatalog = templatesResponse => {
  const payload = normalizeCommunityApiPayload(templatesResponse);
  const templates = payload.templates;
  if (!Array.isArray(templates) || templates.length === 0) {
    return [];
  }

  return [...templates]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(template => ({
      id: template.task_code,
      label: template.display_name || template.task_code,
      sort_order: template.sort_order,
    }));
};

/** Flatten block floors for payload helpers when a single list is needed. */
export const getCatalogFloorsForBlock = (catalog, blockId) => {
  const block = catalog?.blocks?.find(b => b.id === blockId);
  if (block?.floors?.length) return block.floors;
  return [];
};

const buildEmptyTasksForCatalog = taskCatalog => {
  const tasks = Array.isArray(taskCatalog) ? taskCatalog : [];
  return tasks.reduce((acc, task) => {
    acc[task.id] = false;
    return acc;
  }, {});
};

const applyFloorFromApi = (floorState, floor, taskCatalog) => {
  const floorId = floorIdFromNo(floor.floor_no);
  const emptyTasks = buildEmptyTasksForCatalog(taskCatalog);
  const task_codes = floor.task_codes || [];
  task_codes.forEach(code => {
    emptyTasks[code] = true;
  });

  return {
    ...floorState,
    [floorId]: {
      selected: !!floor.selected || task_codes.length > 0,
      tasks: emptyTasks,
      description: floor.description || '',
    },
  };
};

/**
 * @param {object} [personnel]
 * @param {{ blocks?: object[], tasks?: object[] }} catalog
 */
export const personnelToCleaningArea = (personnel, catalog = {}) => {
  const taskCatalog = catalog.tasks || [];
  const selection = createEmptyCleaningAreaSelection();
  const blockIds = new Set();

  if (!personnel || typeof personnel !== 'object') {
    return selection;
  }

  Object.entries(PERSONNEL_API_TO_UI).forEach(([apiKey, uiKey]) => {
    const role = personnel[apiKey];
    if (!role) return;

    selection.selectedEmployeeIdsByCategory[uiKey] = Array.isArray(role.user_ids)
      ? [...role.user_ids]
      : [];
    selection.employeeDescriptionsByCategory[uiKey] = role.description || '';

    if (apiKey === 'supervisors' && role.coverage?.block_ids?.length) {
      role.coverage.block_ids.forEach(id => blockIds.add(id));
    }

    if (!Array.isArray(role.blocks)) return;

    role.blocks.forEach(block => {
      const blockId = block.block_id;
      if (!blockId) return;
      blockIds.add(blockId);

      let floorsState = { ...(selection.floorsByBlock[blockId] || {}) };
      const catalogFloors = getCatalogFloorsForBlock(catalog, blockId);

      if (apiKey === 'security') {
        const floorNos = block.floor_nos || [];
        catalogFloors.forEach(floor => {
          const selected = floorNos.includes(floor.floor_no);
          floorsState[floor.id] = {
            selected,
            tasks: buildEmptyTasksForCatalog(taskCatalog),
            description: '',
          };
        });
        floorNos.forEach(floorNo => {
          const floorId = floorIdFromNo(floorNo);
          if (!floorsState[floorId]) {
            floorsState[floorId] = {
              selected: true,
              tasks: buildEmptyTasksForCatalog(taskCatalog),
              description: '',
            };
          } else {
            floorsState[floorId] = { ...floorsState[floorId], selected: true };
          }
        });
      } else {
        const floorsByNo = {};
        (block.floors || []).forEach(floor => {
          floorsByNo[floor.floor_no] = floor;
        });

        catalogFloors.forEach(floor => {
          const apiFloor = floorsByNo[floor.floor_no];
          if (apiFloor) {
            floorsState = applyFloorFromApi(floorsState, apiFloor, taskCatalog);
          } else if (!floorsState[floor.id]) {
            floorsState[floor.id] = {
              selected: false,
              tasks: buildEmptyTasksForCatalog(taskCatalog),
              description: '',
            };
          }
        });

        (block.floors || []).forEach(floor => {
          floorsState = applyFloorFromApi(floorsState, floor, taskCatalog);
        });
      }

      selection.floorsByBlock[blockId] = floorsState;
    });
  });

  selection.blockIds = Array.from(blockIds);
  if (selection.blockIds.length > 0) {
    selection.activeBlockTab = selection.blockIds[0];
  }

  return selection;
};

const parseTimeFromTimestamp = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;

  return {
    hours,
    minutes,
    period,
    display: `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')} ${period}`,
  };
};

/**
 * @param {object} item Community schedule row from GET /community/schedule
 * @param {{ blocks?: object[], tasks?: object[] }} catalog
 */
export const communityScheduleItemToShift = (item, catalog = {}) => {
  const zeroBasedIndex = resolveZeroBasedShiftIndex(item);

  const startParsed = parseTimeFromTimestamp(item.shift_start_timestamp);
  const endParsed = parseTimeFromTimestamp(item.shift_end_timestamp);

  const personnel = item.personnel || item.schedule_personnel;
  const cleaningArea = personnel
    ? personnelToCleaningArea(personnel, catalog)
    : item.cleaning_area || createEmptyCleaningAreaSelection();

  return {
    ss_id: item.ss_id,
    site_id: item.site_id,
    date: item.date,
    shift_start_time:
      item.shift_start_time || startParsed?.display || '',
    shift_end_time: item.shift_end_time || endParsed?.display || '',
    shift_type: item.shift_type || 'day',
    status: item.status || 'scheduled',
    notes: item.notes || '',
    is_overtime: item.is_overtime || false,
    break_duration: item.break_duration || 60,
    shift_index: zeroBasedIndex + 1,
    schedule_index: zeroBasedIndex,
    isNew: false,
    from_timestamp: item.shift_start_timestamp || '',
    to_timestamp: item.shift_end_timestamp || '',
    guards: [],
    supervisors: [],
    guardNotes: [],
    supervisorNotes: [],
    zoneGuards: {},
    cleaningArea,
    community_summary: item.community_summary,
    schedule_layout: item.schedule_layout || (personnel ? 'personnel' : undefined),
  };
};

/** Pull user rows from assorted API response shapes. */
export const extractScheduleUsersArray = usersResponse => {
  const payload = normalizeCommunityApiPayload(usersResponse);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.staff)) return payload.staff;
  return [];
};

/** @param {object|object[]} usersResponse @param {string} [personnelType] API personnel_type filter used */
export const mapScheduleUsersToEmployees = (usersResponse, personnelType) => {
  const users = Array.isArray(usersResponse)
    ? usersResponse
    : extractScheduleUsersArray(usersResponse);

  return users.map(user => {
    const roleName = user.role_name || user.spec_role || '';
    const isSupervisor =
      personnelType === 'supervisors' ||
      roleName.toLowerCase().includes('supervisor') ||
      roleName.toLowerCase().includes('cluster') ||
      roleName.toLowerCase().includes('manager');

    const displayName =
      user.user_name || user.display_name || user.name || user.employee_name || 'Unknown';

    return {
      user_id: user.user_id || user.cognito_sub,
      employee_id: user.employee_id || user.user_id,
      user_name: displayName,
      name: displayName,
      display_name: displayName,
      role_name: roleName,
      spec_role: user.spec_role || user.role_name || '',
      image_path: user.image_path || '',
      personnel_type: personnelType || user.personnel_type,
      _source: isSupervisor ? 'supervisor' : 'guard',
    };
  });
};

export const normalizeScheduleListResponse = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.shifts)) return data.shifts;
  if (Array.isArray(data?.schedules)) return data.schedules;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};
